/**
 * Auth Provider - 认证业务中心
 *
 * 核心职责：
 * 1. 管理用户认证状态（登录、注销、会话刷新）
 * 2. 包含所有认证相关的业务逻辑（不再依赖外部工具函数）
 * 3. 基于 atom 提供全局状态管理
 * 4. 提供 Hook 接口和原生 JS 接口
 *
 * 设计理念：
 * - 所有认证逻辑集中在此，避免分散在多个工具函数中
 * - Realtime Bridge 和其他模块只是简单代理，不包含业务逻辑
 * - 统一的错误处理和用户反馈机制
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { atom, useAtom, useSetAtom } from "jotai";
import { useRouter } from "nextjs-toploader/app";
import type React from "react";
import { useEffect } from "react";
import { toast } from "@/components/pop-ui/pop-toast";
import { useGlobalRegionChange } from "@/hooks/use-global-region-change";
import { authClient } from "@/lib/auth/auth-client";
import { setAuthMethodsLoader } from "@/lib/error-handler";
import { useRouterPush } from "@/lib/utils.client";

// 定义用户状态类型
interface UserAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  error: string | null;
}

// 定义 Auth 方法类型
interface AuthMethods {
  logout: (options?: {
    redirectUrl?: string;
    clearQueryCache?: boolean;
    showToast?: boolean;
    toastMessage?: string;
  }) => Promise<void>;
  refreshSession: () => Promise<void>;
  navigateWithAuth: (path: string, requireAuth?: boolean) => void;
}

// 创建用户状态的 atom
export const userAuthStateAtom = atom<UserAuthState>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null
});

// 创建 Auth 方法的 atom
export const authMethodsAtom = atom<AuthMethods | null>(null);

// 全局访问器，用于在非组件环境中访问方法
let globalAuthMethods: AuthMethods | null = null;

// 导出给非组件环境使用的方法
export const getAuthMethods = (): AuthMethods | null => {
  return globalAuthMethods;
};

// Hook 用于在组件中访问用户状态
export function useAuth() {
  const $ = _c(3);
  const [authState] = useAtom(userAuthStateAtom);
  const [authMethods] = useAtom(authMethodsAtom);
  let t0;
  if ($[0] !== authMethods || $[1] !== authState) {
    t0 = {
      ...authState,
      ...authMethods
    };
    $[0] = authMethods;
    $[1] = authState;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

// Hook 用于在组件中访问 Auth 方法
export function useAuthMethods() {
  const [methods] = useAtom(authMethodsAtom);
  return methods;
}
export const AuthProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const routerPush = useRouterPush();
  const queryClient = useQueryClient();
  const setAuthState = useSetAtom(userAuthStateAtom);
  const setAuthMethods = useSetAtom(authMethodsAtom);

  // 启用全局地区变化检测
  useGlobalRegionChange();

  // 使用 authClient 的 session hook
  const sessionResult = authClient.useSession();
  const session = sessionResult.data;
  const isLoading = sessionResult.isPending;
  const error = sessionResult.error;

  // 更新用户状态
  useEffect(() => {
    setAuthState({
      isAuthenticated: !!session?.user,
      isLoading,
      user: session?.user || null,
      error: error?.message || null
    });
  }, [session, isLoading, error, setAuthState]);

  // 清除用户数据从本地存储
  const clearUserData = () => {
    try {
      // 清除localStorage
      const keysToRemove = [
        // 添加用户相关的key
      ];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // 清除flash-sale相关的预填信息
      // 动态导入避免循环依赖
      import("@/utils/local-storage").then(({
        clearAllFlashSaleData
      }) => {
        clearAllFlashSaleData();
      }).catch(error_1 => {
        console.error("清除flash-sale数据失败:", error_1);
      });
    } catch (error_0) {
      console.error("清除用户数据失败:", error_0);
    }
  };

  // 创建 Auth 方法
  useEffect(() => {
    const methods: AuthMethods = {
      logout: async (options = {}) => {
        try {
          // 清除查询缓存
          if (options.clearQueryCache) {
            queryClient.clear();
          }

          // 直接处理退出登录逻辑，不依赖外部工具函数
          try {
            // 调用 authClient 退出
            await authClient.signOut();
          } catch (authError) {
            console.warn("Auth client signOut failed:", authError);
            // 继续执行清理逻辑
          }

          // 清除用户相关的localStorage数据
          clearUserData();

          // 显示成功消息
          if (options.showToast && options.toastMessage) {
            toast.success(options.toastMessage, {
              duration: 3000
            });
          }

          // 更新本地状态
          // 注意: 这里会触发 useAuth 状态变化，realtime-provider 会自动断开连接
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null
          });
        } catch (error_2) {
          console.error("Error in auth logout:", error_2);
          setAuthState(prev => ({
            ...prev,
            error: "退出登录失败"
          }));
          if (options.showToast) {
            toast.error("退出登录失败", {
              duration: 3000
            });
          }
          throw error_2;
        }
      },
      refreshSession: async () => {
        try {
          setAuthState(prev_1 => ({
            ...prev_1,
            isLoading: true,
            error: null
          }));
          // 触发 session 刷新
          await authClient.getSession();
        } catch (error_3) {
          console.error("Error refreshing session:", error_3);
          setAuthState(prev_0 => ({
            ...prev_0,
            isLoading: false,
            error: "刷新会话失败"
          }));
          throw error_3;
        }
      },
      navigateWithAuth: (path: string, requireAuth = false) => {
        try {
          if (requireAuth && !session?.user) {
            // 需要认证但未登录，跳转到登录页
            const loginUrl = `/user/login?redirect=${encodeURIComponent(path)}`;
            router.replace(loginUrl);
            return;
          }
          router.replace(path);
        } catch (error_4) {
          console.error("Error in navigateWithAuth:", error_4);
          // 备用处理：使用 router
          router.replace(path);
        }
      }
    };

    // 设置到 atom 中
    setAuthMethods(methods);

    // 设置全局访问器
    globalAuthMethods = methods;

    // 设置error-handler的懒加载器
    setAuthMethodsLoader(() => globalAuthMethods);

    // 清理函数
    return () => {
      globalAuthMethods = null;
      setAuthMethods(null);
    };
  }, [router, routerPush, queryClient, setAuthMethods, setAuthState, session]);
  return <>{children}</>;
};